/**
 * P3-28: EmbassyButton —— race 面板中的大使馆按钮。
 *
 * 迁移自 `classes.diplomacy.ui.EmbassyButton` +
 * `EmbassyButtonController`（js/diplomacy.js:1206-1442）。
 *
 * 行为对齐：
 * - 主按钮点击：建造 embassy（controller.buyItem）
 * - pin 链接：切换 `race.pinned`（ embassyLevel > 0 时生效）
 * - tooltip：自定义 `EmbassyButtonHelper.getTooltipHTML`（含资源概率）
 */
import { useCallback, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { GameButton, GameButtonLink } from "../../components/GameButton";
import { useEmbassyButtonModel, type RaceLike } from "./useTradeModel";

interface EmbassyButtonProps {
  raceName: string;
}

export function EmbassyButton({ raceName }: EmbassyButtonProps) {
  const { snap, modelRef, controller, getTooltip } = useEmbassyButtonModel(raceName);

  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(modelRef.current, event);
    },
    [controller, modelRef],
  );

  const togglePin = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const game = getGame() as (GamePage & { diplomacy?: { get: (n: string) => RaceLike } }) | null;
      const race = game?.diplomacy?.get(raceName);
      if (race && race.embassyLevel > 0) {
        race.pinned = !race.pinned;
      }
    },
    [raceName],
  );

  if (!snap || !snap.visible) {
    return null;
  }

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={() => modelRef.current}
    >
      <GameButtonLink
        link={{
          title: snap.pinned ? "[v]" : "[ ]",
          tooltip: snap.pinned ? t("trade.embassy.pinned") : t("trade.embassy.unpinned"),
          handler: togglePin,
        }}
      />
    </GameButton>
  );
}
